import { Grid, Stack, Typography } from "@mui/material";
import ComponentLayoutWrapper from "src/components/core/ComponentLayoutWrapper";
import PageLayoutController from "src/components/core/PageLayoutController";
import CandBMonogramNavy from "src/icons/CandBMonogramNavy";
import { SignUpFormStyles as SecureBiometricsStyles } from "../components/auth/styles/SignUpForm.styles";
import { Onfido } from "onfido-sdk-ui";
import { useEffect, useReducer, useState } from "react";
import api from "src/store/features/api";
import LoadingComponent from "src/components/application/LoadingComponent";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import { trackEvent } from "src/Amplitude";
import AppFormLegalTerm from "src/components/application/utils/AppFormLegalTerm";
import StyledButton from "src/components/dashboard/StyledButton";

const DESKTOP_LAYOUT = [
  ["APP_BAR", "APP_BAR", "APP_BAR", "APP_BAR", "APP_BAR", "APP_BAR"],
  ["FORM", "FORM", "FORM", "FORM", "FORM", "FORM"],
];

const MOBILE_LAYOUT = [
  ["APP_BAR", "APP_BAR"],
  ["FORM", "FORM"],
];

/**
 * This page is for public secure biometrics
 * If you are looking for Onfido Implementation check `OnfidoVerification.tsx` instead.
 * @returns {*}
 */
const SecureBiometrics = () => {
  const classes = SecureBiometricsStyles();
  const code = new URLSearchParams(window.location.search).get("code") || "";
  const [sdkToken, setSdkToken] = useState<null | string>();
  const [validateToken, { isFetching, isSuccess, isError }] =
    api.useLazyValidateSecureBiometricsQuery();
  const [error, setError] = useState(isError);
  const [workflowRunId, setWorkFlowId] = useState<string | null>();
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useReducer((terms: boolean) => !terms, false);

  useEffect(() => {
    if (code) {
      setLoading(true);
      validateToken({ code })
        .unwrap()
        .then((createApplicantResponse) => {
          setSdkToken(createApplicantResponse.token);
          setWorkFlowId(createApplicantResponse.workflowRunId);
          trackEvent(
            "Validated Secure Biometrics Token",
            { createApplicantResponse },
            "Secure Biometrics",
          );
          setLoading(false);
        })
        .catch(() => {
          setError(true);
        });
    }
  }, [code]);

  useEffect(() => {
    if (sdkToken && workflowRunId && termsAccepted) {
      Onfido.init({
        token: sdkToken,
        workflowRunId,
        language: {
          locale: "en_US",
          phrases: {
            title: "Complete your ID verification",
            description_p_1: "To open a new account, we will need to verify your identity.",
          },
        },
        onComplete: function (data: any) {
          trackEvent("User Initiated Biometrics Capture process", { data }, "Secure Biometrics");
        },
        onError: function (error: any) {
          console.error("Onfido Studio Error: ", error);
        },
      });
    }
  }, [sdkToken, workflowRunId, termsAccepted]);

  const isMobile = useIsMobile();

  // If stuck on loading your data, SF is taking too long to return user data
  if (loading) return <LoadingComponent message="Loading your data" />;

  if (!termsAccepted) {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Stack m={10} spacing={2} maxWidth={600}>
          <AppFormLegalTerm />
          <StyledButton onClick={() => setTermsAccepted()}>Next</StyledButton>
        </Stack>
      </div>
    );
  }

  return (
    <>
      {" "}
      <PageLayoutController
        mobileLayout={MOBILE_LAYOUT}
        desktopLayout={DESKTOP_LAYOUT}
        container={false}>
        {!isMobile && ( // Remove header for mobile devices
          <ComponentLayoutWrapper
            gridArea="APP_BAR"
            fadeInDirection="none"
            state={{ isLoading: isFetching }}
            loadingComponent={<LoadingComponent message="Preparing your biometrics" />}>
            <Grid
              container
              sx={{
                padding: "20px 20px",
                display: "flex",
                alignItems: "flex-start",
              }}>
              <Grid item xs={12}>
                <CandBMonogramNavy height={"32"} width={"28"} />
              </Grid>
            </Grid>
          </ComponentLayoutWrapper>
        )}

        <ComponentLayoutWrapper
          gridArea="FORM"
          fadeInDirection="none"
          state={{}}
          loadingComponent={<></>}>
          {(isError || !code || error) && (
            <div className={classes.container} style={{ justifyContent: "center" }}>
              <Grid
                item
                container
                className={classes.disclaimer}
                sx={{ justifyContent: "center", textAlign: "center" }}>
                <Typography sx={{ color: "#002035" }}>This link is expired or invalid</Typography>
                <Typography className={classes.termsOfUse}>
                  Please reach out to{" "}
                  <a href="mailto:onboarding@calebandbrown.com" className={classes.loginLinkStyle}>
                    Caleb & Brown support
                  </a>{" "}
                  team for a new link.
                </Typography>
              </Grid>
            </div>
          )}
        </ComponentLayoutWrapper>

        {isSuccess && !isFetching && (
          <>
            {/* Main Component */}
            <ComponentLayoutWrapper
              gridArea="FORM"
              fadeInDirection="none"
              state={{}}
              loadingComponent={<></>}>
              <div
                style={{ display: "flex", justifyContent: "center", height: "100vh" }}
                id="onfido-mount"></div>
            </ComponentLayoutWrapper>
          </>
        )}
      </PageLayoutController>
    </>
  );
};

export default SecureBiometrics;
